import { MAX_NICKNAME_LENGTH, normalizeNickname } from '../../server/presence.js';
import type { CryptoLike, SessionStorageLike } from './url.js';

const PROFILE_KEY = 'cards:profile:v1';
const PRESENCE_KEY = 'cards:presence-tab:v1';
const UUID_V4_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/iu;

export interface BrowserProfile {
  profileId: string;
  presenceId: string;
  nickname: string;
}

interface StoredProfile {
  version: 1;
  profileId: string;
  nickname: string;
}

export function loadBrowserProfile(
  localStorage: SessionStorageLike,
  sessionStorage: SessionStorageLike,
  crypto: CryptoLike = globalThis.crypto,
): BrowserProfile {
  const stored = readStoredProfile(localStorage);
  const profile: StoredProfile = stored ?? {
    version: 1,
    profileId: randomUuid(crypto),
    nickname: '',
  };
  writeAndVerify(localStorage, PROFILE_KEY, JSON.stringify(profile));

  let presenceId: string;
  try {
    const existing = sessionStorage.getItem(PRESENCE_KEY);
    presenceId = existing !== null && UUID_V4_PATTERN.test(existing) ? existing : randomUuid(crypto);
    writeAndVerify(sessionStorage, PRESENCE_KEY, presenceId);
  } catch {
    throw new Error('Браузер не может сохранить идентификатор вкладки.');
  }
  return { profileId: profile.profileId, presenceId, nickname: profile.nickname };
}

export function saveProfileNickname(
  localStorage: SessionStorageLike,
  profile: BrowserProfile,
  value: string,
): BrowserProfile {
  const nickname = normalizeNickname(value);
  if (nickname.length === 0) throw new Error('Введите ник перед поиском.');
  if (Array.from(nickname).length > MAX_NICKNAME_LENGTH) {
    throw new Error(`Ник должен быть не длиннее ${MAX_NICKNAME_LENGTH} символов.`);
  }
  if (/\p{C}/u.test(nickname)) throw new Error('Ник содержит недопустимые символы.');
  const stored: StoredProfile = { version: 1, profileId: profile.profileId, nickname };
  try {
    writeAndVerify(localStorage, PROFILE_KEY, JSON.stringify(stored));
  } catch {
    throw new Error('Браузер не может запомнить ник.');
  }
  return { ...profile, nickname };
}

function readStoredProfile(storage: SessionStorageLike): StoredProfile | undefined {
  let raw: string | null;
  try {
    raw = storage.getItem(PROFILE_KEY);
  } catch {
    throw new Error('Браузер не может прочитать сохранённый профиль.');
  }
  if (raw === null) return undefined;
  try {
    const value = JSON.parse(raw) as Partial<StoredProfile>;
    if (value.version === 1 && typeof value.profileId === 'string'
      && UUID_V4_PATTERN.test(value.profileId) && typeof value.nickname === 'string') {
      const nickname = normalizeNickname(value.nickname);
      if (Array.from(nickname).length <= MAX_NICKNAME_LENGTH && !/\p{C}/u.test(nickname)) {
        return { version: 1, profileId: value.profileId, nickname };
      }
    }
  } catch {
    // Replace malformed local-only profile state with a fresh profile.
  }
  return undefined;
}

function writeAndVerify(storage: SessionStorageLike, key: string, value: string): void {
  storage.setItem(key, value);
  if (storage.getItem(key) !== value) throw new Error('storage read-back mismatch');
}

function randomUuid(crypto: CryptoLike): string {
  const bytes = crypto.getRandomValues(new Uint8Array(16));
  bytes[6] = (bytes[6]! & 0x0f) | 0x40;
  bytes[8] = (bytes[8]! & 0x3f) | 0x80;
  const hex = Array.from(bytes, (byte) => byte.toString(16).padStart(2, '0')).join('');
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}
